/**
 * The response chart's tile freshness: whether a tile of the series route
 * already in hand can still change, and how a refresh of it is read and
 * spliced back. Pure: no React, no fetch. `useSeriesTiles` (the chart's tile
 * cache) only wires these to reads.
 *
 * Each rule has a way of being quietly wrong that a test catches and a glance
 * does not:
 *
 *  - A tile is final only on the server's word. The browser's clock never
 *    decides it: a clock a few minutes fast, or a read made between a tile's
 *    end and the next check, would close a tile whose last checks are still
 *    being written, or whose outage has no end yet. The red band then stops
 *    at the tile's edge with a one-interval hole, until the page is reloaded.
 *  - A refresh of a bucketed tile reads only its tail. The overview's tile
 *    can hold sixty days of checks, and it is read again at every new check:
 *    whole, that is tens of thousands of rows a minute for one open page.
 *  - An outage that runs across the splice joins up, the same way two tiles'
 *    halves of one outage do (`mergeSpans`).
 *
 * Imported through `monitoring.ts`, which re-exports this file.
 */
import type { MonitorDownSpan, MonitorSeriesPoint } from "./monitoring";
import { checkIntervalMs, mergeSpans, type SeriesTile } from "./monitoringSeries";
import { MINUTE_MS } from "./monitoringViewport";

/**
 * How long after its time a check can still be missing from a read: the
 * probe, a recheck and an outside look before an outage is confirmed all run
 * before its row is written (`crm-monitoring/lib/apply.ts`), and the site's
 * `lastCheckAt` is written just before the check's own row. Generous on
 * purpose: too long costs one more read of a tile, too short loses a check
 * until the page is reloaded.
 */
export const CHECK_WRITE_MS = 2 * MINUTE_MS;

/**
 * Whether a tile read while the server's newest check was `seenAt` (epoch
 * ms: the site's `lastCheckAt` when the read started, NaN when unknown) can
 * still change, so the next check means reading it again.
 *
 * A tile is final once the newest check is a check interval plus
 * CHECK_WRITE_MS past its end: by then the check that ends an outage crossing
 * the edge exists, and every check before the edge has its row. A read that
 * said its last outage had no end yet (`openEnd`) keeps it open whatever the
 * times say, and so does one that did not know the newest check.
 */
export function tileOpen(end: number, seenAt: number, checkMinutes: number, openEnd: boolean): boolean {
  if (openEnd || !Number.isFinite(seenAt)) return true;
  return end + checkIntervalMs(checkMinutes) + CHECK_WRITE_MS > seenAt;
}

/**
 * Where a refresh of a tile in hand starts reading. A raw tile (six hours at
 * most) is read whole. A bucketed one can hold sixty days of checks, and all
 * a new check can have changed is its tail: the bucket holding its last point
 * (an outage still open there grows) and whatever a check still being
 * written at the last read (`seenAt`) lands in. Aligned to the step, so the
 * tail's buckets are exactly the tile's own; the whole tile when `seenAt` is
 * unknown.
 */
export function refreshFrom(tile: SeriesTile, seenAt: number): number {
  if (tile.step === 0 || !Number.isFinite(seenAt)) return tile.start;
  const stepMs = tile.step * 1000;
  const last = tile.points[tile.points.length - 1];
  const from = Math.min(last ? last.t : tile.start, seenAt - CHECK_WRITE_MS);
  return Math.max(tile.start, Math.floor(from / stepMs) * stepMs);
}

/**
 * The tile with [from, end) replaced by a fresh read of it. Points before
 * `from` stay and the read's take over from there. Runs that started before
 * `from` stay too, and one the read continues (it opens at or before where
 * the old one's provisional end sat) joins it.
 */
export function spliceTile(
  tile: SeriesTile,
  from: number,
  fresh: { points: readonly MonitorSeriesPoint[]; downSpans: readonly MonitorDownSpan[] },
): SeriesTile {
  if (from <= tile.start) return { ...tile, points: [...fresh.points], downSpans: [...fresh.downSpans] };
  return {
    ...tile,
    points: [...tile.points.filter((p) => p.t < from), ...fresh.points],
    downSpans: mergeSpans([...tile.downSpans.filter((s) => s.from < from), ...fresh.downSpans]),
  };
}
